/**
 * Convert a stacked result page (Skills, RoleResearch, Dashboard,
 * InterviewQuestions) into a multi-page PDF.
 *
 * Strategy:
 *   1. Dispatch the existing `resmo:result-section:set-all` event so every
 *      ResultSection in the matching `group` expands. Wait one frame for
 *      the 200ms grid-row animation to complete.
 *   2. Rasterise the sections wrapper with html2canvas at 2× scale.
 *   3. Slice the resulting image across letter-sized pages with jsPDF.
 *   4. Restore the original open/closed state of every section by replaying
 *      the user's prior aria-hidden values.
 *
 * Usage:
 *   import { downloadResultPdf } from "../lib/resultPdf";
 *   downloadResultPdf({
 *     group: "skills-result",
 *     targetSelector: '[data-testid="result-sections"]',
 *     filename: "resmo-salary-report.pdf",
 *     headerTitle: "Senior PM · Salary Report",
 *   });
 */
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

const A4_WIDTH_MM = 210;
const A4_HEIGHT_MM = 297;
const MARGIN_MM = 10;

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

export async function downloadResultPdf({
  group,
  targetSelector,
  filename = "resmo-report.pdf",
  headerTitle = "Resmo report",
}) {
  const target = document.querySelector(targetSelector);
  if (!target) throw new Error(`PDF target not found: ${targetSelector}`);

  // Remember which sections were open BEFORE we force-expand for the export
  // so we can restore the user's UI state when we're done.
  const bodies = target.querySelectorAll('[data-testid$="-body"]');
  const priorState = Array.from(bodies).map((el) => ({
    el,
    hidden: el.getAttribute("aria-hidden") === "true",
  }));

  // 1. Force-expand every section in this group
  window.dispatchEvent(
    new CustomEvent("resmo:result-section:set-all", { detail: { group, open: true } }),
  );
  // 2. Wait for the 200ms grid-row collapse animation + a paint frame
  await sleep(320);

  let canvas;
  try {
    canvas = await html2canvas(target, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#FBF7EE", // matches .rm-paper
      logging: false,
      // Render off-viewport to avoid scroll glitches
      windowWidth: target.scrollWidth,
      windowHeight: target.scrollHeight,
    });
  } finally {
    // 3. Restore the user's prior UI state immediately, even if rasterisation fails.
    // Each body has data-testid="<base>-body"; the matching toggle has
    // data-testid="<base>-toggle". Derive the toggle id by string swap.
    priorState.forEach(({ el, hidden }) => {
      const bodyId = el.getAttribute("data-testid") || "";
      const toggleId = bodyId.endsWith("-body")
        ? `${bodyId.slice(0, -"-body".length)}-toggle`
        : null;
      const toggle = toggleId && document.querySelector(`[data-testid="${toggleId}"]`);
      const currentlyHidden = el.getAttribute("aria-hidden") === "true";
      if (toggle && currentlyHidden !== hidden) toggle.click();
    });
  }

  // 4. Slice the rasterised image across A4 pages
  const pdf = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });
  const printableWidth = A4_WIDTH_MM - MARGIN_MM * 2;
  const imgHeightMm = (canvas.height * printableWidth) / canvas.width;
  const printablePerPage = A4_HEIGHT_MM - MARGIN_MM * 2 - 14; // 14mm reserved for header

  // Render a small header banner on every page
  const drawHeader = (pageIndex, totalPages) => {
    pdf.setFillColor(15, 27, 45); // navy
    pdf.rect(0, 0, A4_WIDTH_MM, 10, "F");
    pdf.setTextColor(255, 193, 69); // mustard
    pdf.setFontSize(9);
    pdf.setFont("helvetica", "bold");
    pdf.text("RESMO", MARGIN_MM, 7);
    pdf.setTextColor(255, 255, 255);
    pdf.setFont("helvetica", "normal");
    pdf.text(headerTitle, MARGIN_MM + 22, 7);
    pdf.setFontSize(8);
    pdf.text(
      `${pageIndex + 1} / ${totalPages}`,
      A4_WIDTH_MM - MARGIN_MM,
      7,
      { align: "right" },
    );
  };

  // Pixel rows per A4 page based on rendered scale
  const pxPerMm = canvas.width / printableWidth;
  const pageHeightPx = Math.floor(printablePerPage * pxPerMm);
  const totalPages = Math.max(1, Math.ceil(canvas.height / pageHeightPx));

  for (let p = 0; p < totalPages; p++) {
    if (p > 0) pdf.addPage();
    drawHeader(p, totalPages);

    const sliceCanvas = document.createElement("canvas");
    sliceCanvas.width = canvas.width;
    sliceCanvas.height = Math.min(pageHeightPx, canvas.height - p * pageHeightPx);
    const ctx = sliceCanvas.getContext("2d");
    ctx.drawImage(
      canvas,
      0,
      p * pageHeightPx,
      canvas.width,
      sliceCanvas.height,
      0,
      0,
      canvas.width,
      sliceCanvas.height,
    );

    const sliceImg = sliceCanvas.toDataURL("image/jpeg", 0.92);
    const sliceHeightMm = (sliceCanvas.height * printableWidth) / canvas.width;
    pdf.addImage(sliceImg, "JPEG", MARGIN_MM, MARGIN_MM + 4, printableWidth, sliceHeightMm);
  }

  pdf.save(filename);
}
